import { useRef, type ReactNode } from 'react';
import type { ChatItem, UserMessageItem } from '../../../contracts/harness.types';
import type { TurnOperation } from '../../../contracts/turn-identity';

import { isVisibleUserMessage } from '../../../data/projects/components/projectChatMessage.helpers';
import { useLatestTurnFloor } from '../../../data/projects/hooks/useLatestTurnFloor';
import { useProjectChatScroll } from '../../../data/projects/hooks/useProjectChatScroll';
import { isClinicianActionableToolError } from '../../../data/projects/integrations/isClinicianActionableToolError';
import {
  deriveLatestTurnNeedsRoomLive,
  deriveShowThinking,
  deriveTurnLifecycle,
  type TurnActivityFlags,
} from '../../../data/projects/lib/turnLifecycle';
import type { SubmitProjectTurnArgs } from '../../../data/projects/submit-project-turn-args';
import {
  computeLatestTodoPlan,
  isSuppressedTodoToolUse,
  type LatestTodoPlan,
} from '../../../data/projects/timeline/agent-todo/computeLatestTodoPlan';
import { hasLiveWorkingSection } from '../../../data/projects/timeline/presentation/derive-timeline-presentation';
import { createProjectTimelinePolicy } from '../../../data/projects/timeline/presentation/tool-axes';
import { OPTIMISTIC_FIRST_TURN_ITEM_ID } from '../../../data/projects/useOptimisticFirstTurn';
import { RunFailedBanner } from '../components/ProjectChatIndicators';
import { ProjectThinkingIndicator } from '../components/ProjectThinkingIndicator';
import { omitReportIssueConfirmation } from '../timeline/omitReportIssueConfirmation';

const PROJECT_CHAT_SCROLL_CONTENT_BOTTOM_PADDING_PX = 24;

/** Same policy the transcript renderer uses, so section liveness matches the Working header. */
const thinkingTimelinePolicy = (latestTodoPlan: LatestTodoPlan | null) =>
  createProjectTimelinePolicy({
    isVisibleUserMessage,
    isSuppressedTodo: (item) => isSuppressedTodoToolUse(item, latestTodoPlan),
    isSuppressedError: (item) => !isClinicianActionableToolError(item),
  });

type StartTurnFn = (command: SubmitProjectTurnArgs) => TurnOperation;

const defaultShouldAnimateUserMessage = () => true;

interface UseProjectConversationTimelineArgs {
  agentRunId?: string | null;
  hasPendingApproval?: boolean;
  hasQueuedTurns?: boolean;
  inFlightDesktopCall?: boolean;
  isLoading?: boolean;
  isStartingSeededTurn?: boolean;
  isSubmitting?: boolean;
  items: ChatItem[];
  renderEmptyTimeline?: boolean;
  runClosed: boolean;
  runFailed: boolean;
  scrollContentBottomPaddingPx?: number;
  shouldAnimateUserMessage?: (item: UserMessageItem) => boolean;
  showPlanProgressDock?: boolean;
  /**
   * Project chat send path. Omitted only by the RPA overlay, which is a
   * read-only transcript and never starts a turn.
   */
  startTurn?: StartTurnFn;
  thinkingPhrases?: string[];
  turnInFlight: boolean;
  /** A stop request is in flight for the current turn. */
  cancelling?: boolean;
  /** See `useProjectChatScroll` — on only for the read-only RPA overlay. */
  followTailByDefault?: boolean;
}

export interface ProjectConversationTimelineModel {
  bottomFadeRef: ReturnType<typeof useProjectChatScroll>['bottomFadeRef'];
  contentRef: ReturnType<typeof useProjectChatScroll>['contentRef'];
  displayItems: ChatItem[];
  followTailRef: ReturnType<typeof useProjectChatScroll>['followTailRef'];
  hasMoreContentBelow: ReturnType<typeof useProjectChatScroll>['hasMoreContentBelow'];
  isEmpty: boolean;
  isInitialLoading: boolean;
  latestTurnFloor: number | null;
  latestTurnHasReply: boolean;
  latestTodoPlan: ReturnType<typeof computeLatestTodoPlan>;
  scrollContentBottomPaddingPx: number;
  scrollContentRef: ReturnType<typeof useProjectChatScroll>['scrollContentRef'];
  scrollRef: ReturnType<typeof useProjectChatScroll>['scrollRef'];
  scrollToBottom: ReturnType<typeof useProjectChatScroll>['scrollToBottom'];
  shouldAnimateUserMessage: (item: UserMessageItem) => boolean;
  showPlanProgressDock: boolean;
  startTurn?: StartTurnFn;
  tailContent: ReactNode;
  turnInFlight: boolean;
}

export const useProjectConversationTimeline = ({
  agentRunId,
  cancelling = false,
  hasPendingApproval = false,
  hasQueuedTurns = false,
  inFlightDesktopCall = false,
  isLoading = false,
  isStartingSeededTurn = false,
  isSubmitting = false,
  items,
  renderEmptyTimeline = false,
  runClosed,
  runFailed,
  scrollContentBottomPaddingPx = PROJECT_CHAT_SCROLL_CONTENT_BOTTOM_PADDING_PX,
  shouldAnimateUserMessage = defaultShouldAnimateUserMessage,
  showPlanProgressDock = true,
  startTurn,
  thinkingPhrases,
  turnInFlight,
  followTailByDefault = false,
}: UseProjectConversationTimelineArgs): ProjectConversationTimelineModel => {
  // The optimistic first-turn bubble is injected upstream by
  // `useOptimisticFirstTurn`, so the caller passes it in `items` (as the sole
  // item while the real timeline is empty). Detect it by its sentinel id — one
  // less prop to thread through the call sites, and it can never drift from
  // what was actually injected.
  const displayItems = omitReportIssueConfirmation(items);
  // Deliberately recomputed on every render because transport updates may mutate
  // the items array in place while a turn streams.
  const latestTodoPlan = computeLatestTodoPlan(displayItems);
  const hasOptimisticFirstTurn = displayItems[0]?.id === OPTIMISTIC_FIRST_TURN_ITEM_ID;

  const lifecycle = deriveTurnLifecycle(displayItems);
  const { latestTurnHasReply, latestTurnIsTerminal, latestUserMessageId } = lifecycle;
  const hasRenderablePendingApproval = displayItems.some(
    (item) => item.kind === 'tool_use' && item.status === 'pending_approval'
  );
  const activityFlags: TurnActivityFlags = {
    hasLiveWorkingSection: false,
    hasPendingApproval: hasRenderablePendingApproval,
    hasQueuedTurns,
    inFlightDesktopCall,
    isPreviewingUserMessage: hasOptimisticFirstTurn,
    isStartingSeededTurn,
    isSubmitting,
    runClosed,
    runFailed,
    turnInFlight,
  };

  const busyNow = turnInFlight || isSubmitting || hasQueuedTurns;
  const wasBusyRef = useRef(false);
  const awaitingFirstReplyRef = useRef(false);
  const staleTailIdAtTurnStartRef = useRef<string | undefined>(undefined);
  const staleTailWasReplyRef = useRef(false);
  const armedBySubmitRef = useRef(false);
  const userMessageIdBeforeBusyRef = useRef<string | null | undefined>(undefined);
  const cancelledWhileAwaitingRef = useRef(false);
  // A later busy edge in the same handoff must not move the stale tail onto
  // this turn's tool. Evidence completes that tool in place; treating it as
  // the pre-turn tail leaves the shimmer up after the turn settles.
  if (busyNow && !wasBusyRef.current && !awaitingFirstReplyRef.current) {
    const tailAlreadyReplied =
      lifecycle.latestTurnHasReply || lifecycle.latestTurnIsTerminal;
    // The new user message has not landed yet, so the reply on the tail is
    // the previous turn's. That is the blank gap, including a turnInFlight
    // edge that never passed through isSubmitting.
    const blankGap = lifecycle.latestUserMessageId === userMessageIdBeforeBusyRef.current;
    if (!tailAlreadyReplied || blankGap) {
      staleTailIdAtTurnStartRef.current = lifecycle.lastItem?.id;
      staleTailWasReplyRef.current = tailAlreadyReplied;
      armedBySubmitRef.current = isSubmitting || hasQueuedTurns;
      awaitingFirstReplyRef.current = true;
    }
  }
  const tailCarriedOverFromBeforeThisTurn =
    awaitingFirstReplyRef.current &&
    staleTailWasReplyRef.current &&
    lifecycle.lastItem?.id === staleTailIdAtTurnStartRef.current;
  const lifecycleForThinking = tailCarriedOverFromBeforeThisTurn
    ? { ...lifecycle, latestReplyHasText: false }
    : lifecycle;
  // Evidence ends on a terminal tool_use, which latestReplyHasText never sees.
  // latestTurnHasReply is the same "the turn replied" gate the thumbs use.
  // A tail only counts as carried over when it was already a reply at arm
  // time — an in-place completion of this turn's tool is the reply.
  if (
    runFailed ||
    runClosed ||
    (!tailCarriedOverFromBeforeThisTurn &&
      (lifecycle.latestTurnHasReply || lifecycle.latestTurnIsTerminal))
  ) {
    awaitingFirstReplyRef.current = false;
  }
  // A turnInFlight blip that never receives a new item is not a send. The
  // submit handoff is the one that must keep the shimmer up across a busy gap.
  if (
    !busyNow &&
    awaitingFirstReplyRef.current &&
    !armedBySubmitRef.current &&
    lifecycle.lastItem?.id === staleTailIdAtTurnStartRef.current
  ) {
    awaitingFirstReplyRef.current = false;
  }
  // A cancel before any output drops the whole turn, marker included, so no
  // reply or terminal item ever lands to release the latch. Release it once
  // the cancel settles instead.
  if (cancelling && awaitingFirstReplyRef.current) {
    cancelledWhileAwaitingRef.current = true;
  }
  if (cancelledWhileAwaitingRef.current && !cancelling && !busyNow) {
    awaitingFirstReplyRef.current = false;
  }
  if (!awaitingFirstReplyRef.current) cancelledWhileAwaitingRef.current = false;
  if (!busyNow && !awaitingFirstReplyRef.current) {
    userMessageIdBeforeBusyRef.current = lifecycle.latestUserMessageId;
  }
  const activityFlagsWithSubmitHandoff = {
    ...activityFlags,
    // The previous turn's section is still the tail until this turn's items
    // land. That is the blank-gap window, so the shimmer stays the progress UI.
    hasLiveWorkingSection:
      !tailCarriedOverFromBeforeThisTurn &&
      hasLiveWorkingSection(
        displayItems,
        thinkingTimelinePolicy(latestTodoPlan),
        turnInFlight
      ),
    isSubmitting:
      activityFlags.isSubmitting || (awaitingFirstReplyRef.current && !busyNow),
  };
  const showThinking = deriveShowThinking(
    lifecycleForThinking,
    activityFlagsWithSubmitHandoff
  );
  wasBusyRef.current = busyNow;

  const scroll = useProjectChatScroll({
    items: displayItems,
    latestTurnHasReply,
    showThinking,
    turnInFlight,
    hasPendingApproval,
    agentRunId: agentRunId ?? undefined,
    startTurn,
    latestTodoPlan,
    showPlanProgressDock,
    followTailByDefault,
  });

  const isInitialLoading =
    isLoading && displayItems.length === 0 && !isStartingSeededTurn && !showThinking;
  const isEmpty =
    !renderEmptyTimeline &&
    !isInitialLoading &&
    !isStartingSeededTurn &&
    !isSubmitting &&
    displayItems.length === 0;

  const liveLatestTurnNeedsRoom = deriveLatestTurnNeedsRoomLive(
    lifecycle,
    activityFlagsWithSubmitHandoff
  );

  const { latestTurnFloor } = useLatestTurnFloor({
    agentRunId,
    cleared: runFailed || runClosed || latestTurnIsTerminal,
    insetPx: scroll.snapTopOffsetPx + scrollContentBottomPaddingPx,
    latestUserMessageId,
    liveNeedsRoom: liveLatestTurnNeedsRoom,
    viewportHeight: scroll.scrollViewportHeight,
  });

  return {
    bottomFadeRef: scroll.bottomFadeRef,
    contentRef: scroll.contentRef,
    displayItems,
    followTailRef: scroll.followTailRef,
    hasMoreContentBelow: scroll.hasMoreContentBelow,
    isEmpty,
    isInitialLoading,
    latestTurnFloor,
    latestTurnHasReply,
    latestTodoPlan,
    scrollContentBottomPaddingPx,
    scrollContentRef: scroll.scrollContentRef,
    scrollRef: scroll.scrollRef,
    scrollToBottom: scroll.scrollToBottom,
    shouldAnimateUserMessage,
    showPlanProgressDock,
    // Overlay omits startTurn; Project chat always supplies one and uses this wrap.
    startTurn: startTurn ? scroll.startTurn : undefined,
    tailContent: (
      <>
        {showThinking ? <ProjectThinkingIndicator messages={thinkingPhrases} /> : null}
        {runFailed ? <RunFailedBanner /> : null}
      </>
    ),
    turnInFlight,
  };
};
